import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { NO_ACCOUNT, POSTING_KINDS, POSTING_KIND_LABELS, REPORT_BASES, REPORT_BASIS_LABELS, formatCents, type ChartOption, type PostingsResult, type ReportBasis } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { SortTh } from '../../components/SortTh';
import { useSession } from '../../session';
import { presets } from './ReportPage';
import './chart.css';

const KIND_LABELS: Record<string, string> = { invoice: 'Rechnung', partial: 'Anzahlung', final: 'Schlussrechnung', credit_note: 'Gutschrift', cancellation: 'Storno', loan: 'Darlehen', hire_purchase: 'Ratenkauf', lease: 'Leasing' };
const day = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;

/**
 * Sachposten (KON-162): die einzelnen Posten hinter den Summen des Finanzreports und des Kontenplans. Jeder Posten ist der Beitrag einer Belegzeile zu einem Konto, mit Link zum
 * Beleg. Wirkia bucht nicht, die Posten entstehen beim Aufruf aus den Rechnungen, mit denselben Regeln wie der Finanzreport, und ergeben dessen Summen. Die Filter stehen in der
 * Adresse, so lässt sich eine Ansicht als Link weitergeben, und der Finanzreport springt mit dem passenden Filter hierher.
 */
export function PostingsPage() {
  const { hasModule, can } = useSession();
  const [params, setParams] = useSearchParams();
  const list = presets();
  const from = params.get('from') ?? list[4]!.from;
  const to = params.get('to') ?? list[4]!.to;
  const basis = (params.get('basis') ?? '') as '' | ReportBasis;
  const account = params.get('account') ?? '';
  const kind = params.get('kind') ?? '';
  const q = params.get('q') ?? '';
  const sort = params.get('sort') ?? '-date';
  const page = Number(params.get('page') ?? 1);
  const valid = !!from && !!to && to >= from;

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v ? merged.set(k, v) : merged.delete(k));
    if (!('page' in next)) merged.delete('page');
    setParams(merged, { replace: true });
  };
  const query = { from, to, basis, account, kind, q, sort };
  const result = useQuery({
    queryKey: ['chart-postings', query, page],
    queryFn: () => api<PostingsResult>('GET', `/chart/postings${queryString({ ...query, page, perPage: 50 })}`),
    enabled: valid,
    placeholderData: keepPreviousData,
  });
  const options = useQuery({
    queryKey: ['chart-options'],
    queryFn: async () => (await api<{ data: ChartOption[] }>('GET', '/chart/options')).data,
    enabled: hasModule('chart') && can('chart.read'),
    staleTime: 60_000,
  });
  const r = result.data;
  const csv = `/api/v1/chart/postings${queryString({ ...query, format: 'csv' })}`;
  const accountOptions = options.data ?? [];
  const documentLink = (p: PostingsResult['data'][number]) => (p.source === 'sales' ? `/rechnungen/${p.documentId}` : p.source === 'financing' ? `/finanzierungen/${p.documentId}` : p.source === 'payroll' ? `/loehne/${p.documentId}` : `/eingangsrechnungen/${p.documentId}`);
  const filtered = !!(account || kind || q);

  return (
    <>
      <div className="page-head">
        <h1>Sachposten</h1>
        <span className="muted">Die Posten hinter den Summen</span>
      </div>

      <section className="card report-filter" aria-label="Filter">
        <div className="report-presets" role="group" aria-label="Zeitraum wählen">
          {list.map((p) => (
            <button key={p.label} type="button" className={`btn btn--sm ${from === p.from && to === p.to ? 'btn--primary' : 'btn--secondary'}`} onClick={() => update({ from: p.from, to: p.to })}>{p.label}</button>
          ))}
        </div>
        <div className="report-fields">
          <label className="field"><span>Von</span><input className="input" type="date" value={from} onChange={(e) => update({ from: e.target.value })} /></label>
          <label className="field"><span>Bis</span><input className="input" type="date" value={to} onChange={(e) => update({ to: e.target.value })} /></label>
          <label className="field">
            <span>Konto</span>
            {accountOptions.length > 0 ? (
              <select className="input" value={account} onChange={(e) => update({ account: e.target.value })}>
                <option value="">Alle Konten</option>
                <option value={NO_ACCOUNT}>Ohne Konto</option>
                {account && account !== NO_ACCOUNT && !accountOptions.some((o) => o.number === account) && <option value={account}>{account}</option>}
                {accountOptions.map((o) => <option key={o.number} value={o.number}>{o.number} {o.name}</option>)}
              </select>
            ) : (
              <input className="input" value={account} onChange={(e) => update({ account: e.target.value.trim() })} placeholder="Kontonummer, leer = alle" />
            )}
          </label>
          <label className="field">
            <span>Art</span>
            <select className="input" value={kind} onChange={(e) => update({ kind: e.target.value })}>
              <option value="">Erlöse und Aufwand</option>
              {POSTING_KINDS.map((k) => <option key={k} value={k}>{POSTING_KIND_LABELS[k]}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Zuordnung</span>
            <select className="input" value={basis} onChange={(e) => update({ basis: e.target.value })}>
              <option value="">Wie in der Einrichtung</option>
              {REPORT_BASES.map((b) => <option key={b} value={b}>{REPORT_BASIS_LABELS[b]}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Suchen</span>
            <input className="input" type="search" defaultValue={q} key={q} placeholder="Belegnummer, Name, Konto" onKeyDown={(e) => { if (e.key === 'Enter') update({ q: e.currentTarget.value.trim() }); }} onBlur={(e) => e.currentTarget.value.trim() !== q && update({ q: e.currentTarget.value.trim() })} />
          </label>
        </div>
        <div className="report-downloads">
          {r && valid && <a className="btn btn--secondary btn--sm" href={csv}>CSV herunterladen (Excel)</a>}
          {filtered && <button type="button" className="btn btn--secondary btn--sm" onClick={() => update({ account: '', kind: '', q: '' })}>Filter zurücksetzen</button>}
          <Link className="btn btn--secondary btn--sm" to={`/finanzreport${queryString({ })}`}>Zum Finanzreport</Link>
        </div>
        {!valid && <p className="alert alert--error" role="alert">Das Ende liegt vor dem Beginn.</p>}
      </section>

      {result.isError && <div className="alert alert--error" role="alert">{(result.error as Error).message}</div>}
      {r && (
        <p className="muted small">
          {day(r.from)} bis {day(r.to)} · Nach {r.basis === 'payment' ? 'Zahlungsdatum' : 'Rechnungsdatum'} · {r.meta.total.toLocaleString('de-DE')} Posten
          {r.totals.revenueNetCents !== 0 && <> · Erlöse netto <strong>{formatCents(r.totals.revenueNetCents)}</strong></>}
          {r.totals.expenseNetCents !== 0 && <> · Aufwand netto <strong>{formatCents(r.totals.expenseNetCents)}</strong></>}
        </p>
      )}

      <div className="card table-card">
        <table className="table table--cards postings">
          <thead>
            <tr>
              <SortTh label="Datum" sortKey="date" sort={sort} onSort={(s) => update({ sort: s })} first="desc" />
              <th>Art</th>
              <SortTh label="Konto" sortKey="account" sort={sort} onSort={(s) => update({ sort: s })} />
              <SortTh label="Beleg" sortKey="document" sort={sort} onSort={(s) => update({ sort: s })} />
              <th>Kunde oder Lieferant</th>
              <th>Steuer</th>
              <SortTh label="Netto" sortKey="amount" sort={sort} onSort={(s) => update({ sort: s })} first="desc" className="num" />
              <th className="num">Steuerbetrag</th>
              <th className="num">Brutto</th>
            </tr>
          </thead>
          <tbody>
            {result.isLoading && <tr><td colSpan={10} className="muted">Wird geladen …</td></tr>}
            {r?.data.length === 0 && <tr><td colSpan={10} className="empty">{filtered ? 'Keine Posten zu diesem Filter.' : 'Keine Posten in diesem Zeitraum.'}</td></tr>}
            {r?.data.map((p) => (
              <ExpandRow
                key={p.id}
                className={p.accountNumber ? '' : 'row--muted'}
                summary={<Summary title={<Link to={documentLink(p)} className="strong-link">{p.documentNumber ?? 'Ohne Nummer'}</Link>} aside={formatCents(p.netCents)} line2={`${day(p.date)} · ${p.accountNumber ?? 'ohne Konto'} ${p.accountNumber ? p.accountName : ''}`.trim()} line3={[p.contactName, KIND_LABELS[p.documentKind] ?? p.documentKind].filter(Boolean).join(' · ')} />}
              >
                <td className="cell-desktop-title">{day(p.date)}</td>
                <td data-label="Art">{POSTING_KIND_LABELS[p.kind]}</td>
                <td data-label="Konto">{p.accountNumber ? <><strong>{p.accountNumber}</strong> <span className="muted small">{p.accountName}</span></> : <span className="muted">{p.accountName}</span>}</td>
                <td data-label="Beleg"><Link to={documentLink(p)} className="strong-link">{p.documentNumber ?? 'Ohne Nummer'}</Link><div className="muted small">{KIND_LABELS[p.documentKind] ?? p.documentKind}</div></td>
                <td data-label="Kunde oder Lieferant">{p.contactName ?? '–'}</td>
                <td data-label="Steuer">{p.taxCode}</td>
                <td data-label="Netto" className="num">{formatCents(p.netCents)}{p.note && <div className="muted small postings__note">{p.note}</div>}</td>
                <td data-label="Steuerbetrag" className="num">{formatCents(p.taxCents)}</td>
                <td data-label="Brutto" className="num">{formatCents(p.grossCents)}</td>
              </ExpandRow>
            ))}
          </tbody>
          {r && r.data.length > 0 && (
            <tfoot>
              {(!kind || kind === 'revenue') && r.totals.revenueNetCents !== 0 && (
                <tr>
                  <th colSpan={6}>Summe Erlöse</th>
                  <th className="num">{formatCents(r.totals.revenueNetCents)}</th>
                  <th className="num">{formatCents(r.totals.revenueTaxCents)}</th>
                  <th className="num">{formatCents(r.totals.revenueGrossCents)}</th>
                </tr>
              )}
              {(!kind || kind === 'expense') && r.totals.expenseNetCents !== 0 && (
                <tr>
                  <th colSpan={6}>Summe Aufwand</th>
                  <th className="num">{formatCents(r.totals.expenseNetCents)}</th>
                  <th className="num">{formatCents(r.totals.expenseTaxCents)}</th>
                  <th className="num">{formatCents(r.totals.expenseGrossCents)}</th>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div>
      {r && r.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {r.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= r.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
      <p className="muted small">Erlöse und Aufwand werden getrennt summiert, sie sind keine Gegenbuchungen. Gutschriften und Stornos tragen ein Minus und heben die Rechnung auf, auf die sie sich beziehen. Es ist keine Buchführung, Wirkia bucht nicht.</p>
    </>
  );
}
